import { SelectField } from '#app/components/form/select-field.tsx'
import { RecallEventAdd } from '#app/routes/mocra+/_form+/components/recall-events/add.tsx'
import { RecallEventCard } from '#app/routes/mocra+/_form+/components/recall-events/card.tsx'
import { type RecallEventDraft } from '#app/routes/mocra+/_form+/components/recall-events/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type Option = {
	readonly value: string
	readonly label: string
}

type RecallHistoryStatus = string
type RecallEventItem = {
	readonly draft: RecallEventDraft
	readonly errors: readonly string[]
}

type Props = {
	readonly resources: SectionResources
	readonly statusOptions: ReadonlyArray<Option>
	readonly eventTypeOptions: ReadonlyArray<Option>
	readonly eventOutcomeOptions: ReadonlyArray<Option>
	readonly status: RecallHistoryStatus
	readonly statusErrors: readonly string[]
	readonly eventsError: string | null
	readonly events: ReadonlyArray<RecallEventItem>
	readonly showEvents: boolean
	readonly onStatusChange: (value: RecallHistoryStatus) => void
	readonly onCreateEvent: (draft: RecallEventDraft) => void
	readonly onEditEvent: (index: number) => void
	readonly onRemoveEvent: (index: number) => void
	readonly defaultEventDraft: RecallEventDraft
}

export function RecallHistorySection({
	resources,
	statusOptions,
	eventTypeOptions,
	eventOutcomeOptions,
	status,
	statusErrors,
	eventsError,
	events,
	showEvents,
	onStatusChange,
	onCreateEvent,
	onEditEvent,
	onRemoveEvent,
	defaultEventDraft,
}: Props) {
	return (
		<Section
			title="Recall history"
			description="Indicate whether you've executed recalls, withdrawals, or only drills. Add entries for each event so we can tailor follow-up actions and templates."
			mocraSections={resources.mocraSections}
			templates={resources.templates}
			childrenClassName="space-y-4"
		>
			<SelectField
				labelProps={{
					children: 'What recall experience do you have so far?',
				}}
				selectProps={{
					value: status,
					onChange: (event) => onStatusChange(event.target.value),
				}}
				options={statusOptions}
				errors={statusErrors.length > 0 ? [...statusErrors] : undefined}
			/>
			{showEvents ? (
				<div className="space-y-3">
					{eventsError ? (
						<p className="text-destructive text-sm">{eventsError}</p>
					) : null}
					{events.length > 0 ? (
						events.map((event, index) => (
							<RecallEventCard
								key={`recall-event-${index}`}
								event={event.draft}
								errors={event.errors}
								onEdit={() => onEditEvent(index)}
								onRemove={() => onRemoveEvent(index)}
							/>
						))
					) : (
						<p className="text-muted-foreground text-sm">
							Document each recall, withdrawal, or drill so we can tailor the
							playbooks you receive.
						</p>
					)}
					<RecallEventAdd
						onSubmit={onCreateEvent}
						typeOptions={eventTypeOptions}
						outcomeOptions={eventOutcomeOptions}
						defaultValue={defaultEventDraft}
					/>
				</div>
			) : null}
		</Section>
	)
}
